import React, { useEffect, useRef, useState } from 'react';
import aiProgrammingImg from '../files/figures/ai_programming.png';
import learningToDeferImg from '../files/figures/learning_to_defer.png';
import onboardingImg from '../files/figures/onboarding.png';
import magenticImg from '../files/figures/magenticexample.png';
import faraLoopImg from '../files/figures/fara_agentic_loop.png';

const sections = [
  { id: 'computer-use', title: 'Computer Use Agents' },
  { id: 'agents', title: 'Interacting with AI Agents' },
  { id: 'programming', title: 'AI-Assisted Programming' },
  { id: 'defer', title: 'Learning to Defer to Humans' },
  { id: 'onboarding', title: 'Teaching Humans How to Interact with AI' },
];

// The site uses a HashRouter, so in-page "#id" links would change the route;
// scroll to sections programmatically instead.
const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Tracks which section is currently being read
const useActiveSection = () => {
  const [active, setActive] = useState(sections[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = sections[0].id;
      for (const { id } of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight / 3) current = id;
      }
      // At the bottom of the page the last (short) section may never reach the top third
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = sections[sections.length - 1].id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return active;
};

const SectionNav = ({ active, variant }) => {
  const chipsRef = useRef(null);

  // Keep the active chip visible in the horizontally scrolling mobile nav
  useEffect(() => {
    const row = chipsRef.current;
    const chip = row?.querySelector(`[data-section="${active}"]`);
    if (!row || !chip || !row.offsetParent) return;
    row.scrollTo({ left: chip.offsetLeft - row.offsetLeft - 16, behavior: 'smooth' });
  }, [active]);

  if (variant === 'side') {
    // Desktop: sticky side navigation
    return (
      <nav className="hidden lg:block w-56 flex-shrink-0" aria-label="Research sections">
        <div className="sticky top-24 pt-2">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">On this page</p>
          <ul className="space-y-1 border-l border-zinc-200">
            {sections.map(({ id, title }) => (
              <li key={id}>
                <button
                  onClick={() => scrollToSection(id)}
                  className={`-ml-px block w-full text-left border-l-2 pl-4 py-1 text-sm leading-snug transition-colors ${
                    active === id
                      ? 'border-zinc-900 text-zinc-900 font-medium'
                      : 'border-transparent text-zinc-500 hover:text-zinc-900 hover:border-zinc-300'
                  }`}
                >
                  {title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    );
  }

  // Mobile / tablet: horizontally scrollable section chips pinned under the header
  return (
    <nav className="lg:hidden sticky top-14 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 mb-8 bg-white/90 backdrop-blur-md border-b border-zinc-200/70" aria-label="Research sections">
      <div ref={chipsRef} className="flex gap-2 overflow-x-auto scrollbar-hide">
        {sections.map(({ id, title }) => (
          <button
            key={id}
            data-section={id}
            onClick={() => scrollToSection(id)}
            className={`chip whitespace-nowrap flex-shrink-0 ${
              active === id ? '!bg-zinc-900 !text-white !border-zinc-900' : ''
            }`}
          >
            {title}
          </button>
        ))}
      </div>
    </nav>
  );
};

const ResearchPage = () => {
  const active = useActiveSection();

  return (
  <div className="flex gap-12">
    <div className="max-w-3xl min-w-0 flex-1 prose-body text-zinc-700">
    <h1 className="page-title mb-6 lg:mb-10">Research</h1>
    <SectionNav active={active} />


    <h2 id="computer-use" className="scroll-mt-28 text-xl md:text-2xl font-semibold mt-16 first-of-type:mt-0 mb-5 pt-10 border-t border-zinc-100 first-of-type:border-0 first-of-type:pt-0">Computer Use Agents</h2>
    <img src={faraLoopImg} alt="Computer Use Agents" className="mb-6 w-full mx-auto rounded-xl ring-1 ring-zinc-200 bg-white p-3" />

    Computer use agents (CUAs) aim to help people complete any task on a computer, using whatever tools work best, from the keyboard and mouse to the terminal.
    My work on CUAs has mostly focused on building environments and verifiers for training them with reinforcement learning.
    I am also interested in harness design and in scaling agents' capabilities through subagents.
    At Microsoft Research, I co-led the Fara family of open-weight computer use models, and I am now continuing this work on the post-training team at Microsoft AI.

    <span className="block h-5" />

    <span className="block mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">My work includes:</span>
    <ul>
      <li>
        <a href="https://arxiv.org/abs/2606.20785">Fara-1.5: Scalable Learning Environments for Computer Use Agents</a> [arxiv 2026] - introduces FaraGen1.5, a scalable data pipeline of environments, solvers and verifiers, used to train the Fara1.5 models (4B, 9B, 27B), each state of the art for its size on browser-use benchmarks.
      </li>
      <li>
        <a href="https://arxiv.org/abs/2607.28074">Echoverse: Deep, Evolving Environments for Training Computer-Use Agents at Scale</a> [arxiv 2026] - builds deep, stateful training environments that improve alongside the model and serve for both supervised training and reinforcement learning.
      </li>
      <li>
        <a href="https://arxiv.org/abs/2511.19663">Fara-7B: An Efficient Agentic Model for Computer Use</a> [arxiv 2025] - introduces FaraGen, a synthetic data generation system for multi-step web tasks, used to train Fara-7B, a small on-device computer use model that outperforms models of comparable size.
      </li>
    </ul>

    <span className="block h-5" />

    <h2 id="agents" className="scroll-mt-28 text-xl md:text-2xl font-semibold mt-16 first-of-type:mt-0 mb-5 pt-10 border-t border-zinc-100 first-of-type:border-0 first-of-type:pt-0">Interacting with AI Agents</h2>
    <img src={magenticImg} alt="AI-Agents" className="mb-6 w-full md:w-3/4 mx-auto rounded-xl ring-1 ring-zinc-200 bg-white p-3" />

    Recently, we're seeing a new trend emerge where large language models are augmented with tools that allow them to interact with the digital world.
    Such a system, an LLM augmented with tools in a loop, is refered to as an AI agent more commonly. 
    This ability to interact with and observe the digital world creates new challenges.
    I have focused on how can  AI agents interact with each other to achieve a common goal and how should such AI agents interact with the human that defines their goal.
    I have also been involved in efforts to create a framework to develop such AI agents, <a href="https://github.com/microsoft/autogen"> Microsoft AutoGen</a>.


    <span className="block h-5" />

    <span className="block mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">My previous work includes:</span>
    <ul>
      <li>
        <a href="https://arxiv.org/abs/2609.33113">ParallelPilot: Supporting Coordination and Monitoring in Parallel AI Coding</a> [arxiv 2026] - identified key patterns to enable developers to scale their ability to supervise multiple coding agents.
      </li>
      <li>
        <a href="https://github.com/microsoft/magentic-ui">Magentic-UI: A human-centered interface for interacting with AI agents</a>
      </li>
      <li>
        <a href="https://arxiv.org/pdf/2411.04468?">Magentic-one: A generalist multi-agent system for solving complex tasks</a> [arxiv 2024] - proposes an architecture consisting of a lead orchestrator agent that delegates tasks to other agents to solve complex tasks that require web navigation and code execution.
      </li>
      <li>
        <a href="https://arxiv.org/pdf/2412.10380">Challenges in Human-Agent Communication</a> [arxiv 2024] - lists 10 challenges when AI agents start to interact with humans
      </li>
      <li>
        <a href="https://github.com/microsoft/autogen">Microsoft AutoGen</a> - an open source programming framework for agentic AI.
      </li>
    </ul>

    <span className="block h-5" />

    <h2 id="programming" className="scroll-mt-28 text-xl md:text-2xl font-semibold mt-16 first-of-type:mt-0 mb-5 pt-10 border-t border-zinc-100 first-of-type:border-0 first-of-type:pt-0">AI-Assisted Programming</h2>
    <img src={aiProgrammingImg} alt="AI-Assisted Programming" className="mb-6 w-full md:w-3/4 mx-auto rounded-xl ring-1 ring-zinc-200 bg-white p-3" />

    AI has changed the way we write code for a lot of us. LLMs have been adopted by millions of programmers to help them write their code.
    The forms of AI-assisted programming are diverse, ranging from code completions (original GitHub Copilot), chat-based general-purpose AI assistants (ChatGPT), and newer forms that integrate AI agents and code edits. 
    The goal of my research is to understand how people interact with these AI systems, how we can evaluate their impact on programmers' performance, and how we can design new interfaces that integrate AI.
    <span className="block h-5" />

    <span className="block mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">My previous work includes:</span>
    <ul>
      <li>
        <a href="https://arxiv.org/abs/2210.14306">Reading between the lines: Modeling user behavior and costs in AI-assisted programming</a> [CHI 2024] - profiles how people interact with code autocomplete systems and introduces a taxonomy of interactions (CUPS).
      </li>
      <li>
        <a href="https://arxiv.org/abs/2306.04930">When to show a suggestion? Integrating human feedback in AI-assisted programming</a> [AAAI 2024] - introduces a mechanism (CDHF) for when to display AI code suggestions based on human feedback
      </li>
      <li>
        <a href="https://arxiv.org/abs/2404.02806">The RealHumanEval: Evaluating Large Language Models' Abilities to Support Programmers</a> - proposes a human evaluation method to assess how different AI models (GPT-3.5, CodeLlama,...) improve programmer productivity.
      </li>
      <li>
        <a href="https://arxiv.org/abs/2410.04596">Need Help? Designing Proactive AI Assistants for Programming</a> - introduces a proactive chat-based AI assistant that also integrates chat suggestions into the code.
      </li>
      <li>
        <a href="https://openreview.net/pdf?id=0nRcZeeE5f">Simulating Iterative Human-AI Interaction in Programming with LLMs</a> - proposes a simulation environment for evaluating AI-assisted programming interfaces
      </li>
    </ul>

    <span className="block h-5" />
    <h2 id="defer" className="scroll-mt-28 text-xl md:text-2xl font-semibold mt-16 first-of-type:mt-0 mb-5 pt-10 border-t border-zinc-100 first-of-type:border-0 first-of-type:pt-0">Learning to Defer to Humans</h2>
    <img src={learningToDeferImg} alt="Learning to Defer to Humans" className="mb-6 w-full md:w-3/4 mx-auto rounded-xl ring-1 ring-zinc-200 bg-white p-3" />
    How do we combine AI systems and human decision-makers to both reduce error and alleviate
the burden on the human? AI systems are starting to be frequently used in combination with human
decision-makers, including in high-stakes settings like healthcare and content moderation. A
possible way to combine the human and the AI is to learn a 'rejector' that queries either the human
or the AI to predict on each input. This allows us to route examples to
the AI model, where it outperforms the human, so as to simultaneously reduce error and human
effort. Moreover, this formulation allows us to jointly optimize the AI so as to complement the
human’s weaknesses, and to optimize the rejector to allow the AI to defer when it is unable to
predict well. This problem is referred to as the 'learning to defer' problem.
<span className="block h-5" />

    <span className="block mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">My previous work includes:</span>
    <ul>
      <li>
        <a href="https://arxiv.org/abs/2006.01862">Consistent estimators for learning to defer to an expert</a> [ICML 2020] - introduces a consistent estimator for learning to defer.
      </li>
      <li>
        <a href="https://arxiv.org/abs/2207.09584">Sample efficient learning of predictors that complement humans</a> [ICML 2022] - proves the theoretical benefits of learning to defer and introduces active learning algorithms
      </li>
      <li>
        <a href="https://arxiv.org/abs/2301.06197">Who Should Predict? Exact Algorithms For Learning to Defer to Humans</a> [AISTATS 2023] - introduces an improved estimator for learning to defer and an evaluation framework.
      </li>
    </ul>

    An article on this work can be found <a href="https://www.csail.mit.edu/news/ai-systems-work-wdoctors-and-know-when-step" target="_blank" rel="noopener">here</a>.

    <span className="block h-5" />
    <h2 id="onboarding" className="scroll-mt-28 text-xl md:text-2xl font-semibold mt-16 first-of-type:mt-0 mb-5 pt-10 border-t border-zinc-100 first-of-type:border-0 first-of-type:pt-0">Teaching Humans How to Interact with AI</h2>
    <img src={onboardingImg} alt="Teaching Humans How to Interact with AI" className="mb-6 w-full md:w-3/4 mx-auto rounded-xl ring-1 ring-zinc-200 bg-white p-3" />

     A key question is how does the human
expert know when to rely on the AI for advice. The literature on human-AI collaboration has often
revealed that humans often underperform expectations when working with AI systems.
The negative results of human-AI performance may be attributed to a few possible reasons. First,
humans can have miscalibrated expectations about AI’s ability, which leads to over-reliance.
Second, the cost of verifying the AI’s answer with explanations might be too high thus providing a
bad cost-benefit tradeoff for the human and leading to either over-reliance or under-reliance on
the AI. Finally, the AI explanations do not enable the human to verify the correctness of the AI’s
answer and thus are not as useful for human-AI collaboration.
<span className="block h-5" />
We make the case for the need to initially onboard the human decision-maker on when and
when not to rely on the automated agent. We propose that before an AI agent is deployed to assist
a human decision-maker, the human is taught through a tailored onboarding phase how to make
decisions with the help of the AI. The purpose of the onboarding is to help the human understand
when to trust the AI and how the AI can complement their abilities. 
<span className="block h-5" />

    <span className="block mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">My previous work includes:</span>
    <ul>
      <li>
        <a href="https://arxiv.org/abs/2111.11297">Teaching humans when to defer to a classifier via exemplars</a> [AAAI 2022]
      </li>
      <li>
        <a href="https://arxiv.org/abs/2311.01007">Effective human-AI teams via learned natural language rules and onboarding</a> [NeurIPS 2023]
      </li>
    </ul>

    An article on this work can be found <a href="https://news.mit.edu/2023/automated-system-teaches-collaborate-ai-assistant-1208" target="_blank" rel="noopener">here</a>.



    <p className="mt-12 pt-8 border-t border-zinc-100"> You can find my full list of publications on my <a href= "https://scholar.google.com/citations?user=XCfZyIkAAAAJ&hl=en"> Google Scholar</a> </p>
    </div>
    <SectionNav active={active} variant="side" />
  </div>
  );
};

export default ResearchPage;
